import { useMemo } from "react";
import { CheckIcon, TrustpilotMini } from "staysure-component-library";

import RichText from "@/components/atoms/rich-text/rich-text";
import withBrand from "@/components/with-brand";
import { RenewalSectionType } from "@/schema/renewal-page/page-content.schema";
import { BrandKey } from "@/types/common-types";
import { RenewalRightPanelPageDataType, ReviewData } from "@/types/renewals";

import MedicalConditionsFooter from "./medical-conditions-footer";
import Styles from "./renewal-right-panel-info-card.module.css";
import { useRightPanelBrandStyles } from "./right-panel-info-card-utils";

type RenewalRightPanelInfoCardProps = {
  testId: string;
  pageData: RenewalRightPanelPageDataType;
  reviewData: ReviewData;
  activeSection: string;
  brand?: BrandKey;
};

function RenewalRightPanelInfoCard({
  testId,
  activeSection,
  pageData,
  reviewData,
  brand = "staysure",
}: Readonly<RenewalRightPanelInfoCardProps>) {
  const { states, handlers, styles } = useRightPanelBrandStyles(
    pageData,
    activeSection,
    brand
  );

  const { bgAvailable, bgVisible } = states;
  const { setBgVisible, selectedSection } = handlers;
  const { getBgStyles, brandStyles } = styles;

  const rightPanelCardContent = useMemo(() => {
    const cardContent = selectedSection();
    if (
      cardContent?.uniqueId === RenewalSectionType.Values["medical-conditions"]
    ) {
      return (
        <div className="flex flex-col items-center md:justify-start max-w-[248px] min-h-[390px] md:max-w-[432px] md:min-h-[296px] lg:max-w-[320px] lg:min-h-[355px] md:gap-spacing-xxl gap-4">
          <div className="flex flex-col">
            <p
              className={`${brandStyles?.titleColor} md:text-3xl text-xl leading-[30px] md:leading-custom-size-xl font-header-font whitespace-break-spaces text-center xl:text-left lg:pt-5`}
            >
              {cardContent?.title}
            </p>

            <div className="flex justify-center lg:justify-start text-center xl:text-left">
              <div
                className={`${brandStyles?.descriptionColor} text-xs font-normal leading-[27px]`}
              >
                <RichText
                  richText={cardContent?.description ?? ""}
                  className={Styles.richTextContent}
                  bulletIcon={
                    <CheckIcon width={24} height={24} fill="#FFD873" />
                  }
                />
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <article
        className={`${Styles.trustpilotWrapper} flex flex-col items-center md:justify-start lg:max-w-[320px] lg:h-[452px] md:gap-spacing-xxl md:max-w-[608px] md:h-[352px] max-w-[248px] h-[442px] gap-spacing-l`}
      >
        <TrustpilotMini
          stars={reviewData.stars}
          score={reviewData.score}
          link={pageData.trustPilotUrl ?? ""}
          totalReviews={reviewData.totalReviews}
          trustpilotClassName={brandStyles?.trustPilot}
        />

        <div>
          <p
            className={`${brandStyles?.titleColor} md:text-3xl text-xl md:leading-custom-size-xl font-header-font text-center leading-custom-size-l/xl`}
          >
            {cardContent?.title}
          </p>
          {activeSection === "policy-extras" && (
            <RichText
              richText={cardContent?.description ?? ""}
              className={`font-body-font ${
                brand === "avanti"
                  ? "leading-[27px]"
                  : "leading-custom-size-xxl"
              } text-xs ${brandStyles?.descriptionColor} text-center mt-spacing-l`}
            />
          )}
        </div>
      </article>
    );
  }, [
    selectedSection,
    reviewData.stars,
    reviewData.score,
    reviewData.totalReviews,
    pageData.trustPilotUrl,
    brandStyles?.trustPilot,
    brandStyles?.titleColor,
    brandStyles?.descriptionColor,
    activeSection,
    brand,
  ]);

  return (
    <div
      className="flex flex-col relative justify-between items-center rounded-radius-xxl xl:max-w-[384px] lg:min-h-[580px] md:min-h-[480px] w-full min-h-[570px]"
      data-testid={testId}
      style={!bgAvailable ? getBgStyles.style : {}}
    >
      {bgAvailable && !bgVisible && (
        <div className="rounded-radius-xxl lg:max-w-[384px] lg:min-h-[580px] md:min-h-[480px] w-full min-h-[570px] animate-loading z-[2]" />
      )}
      {bgAvailable && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={`${getBgStyles.url}`}
          alt="Background"
          className={`absolute top-0 left-0 ${
            brand === "avanti" ? "object-cover object-[50%_75%]" : "object-fill"
          }  xl:max-w-[384px] lg:min-h-[580px] md:min-h-[480px] w-full min-h-[570px] h-full z-[1] rounded-radius-xxl`}
          onLoad={() => {
            setBgVisible(true);
          }}
        />
      )}
      {bgVisible && bgAvailable && (
        <div
          style={getBgStyles.style}
          className="absolute inset-0 rounded-radius-xxl z-[3]"
        />
      )}
      {((bgAvailable && bgVisible) || bgAvailable === false) && (
        <>
          <div className="py-spacing-xxl lg:py-spacing-4xl z-[4]">
            {rightPanelCardContent}
          </div>
          {activeSection === RenewalSectionType.Values["medical-conditions"] &&
            (selectedSection()?.footerTrustpilotMiniImage &&
            selectedSection()?.footerTrustpilotMiniUrl ? (
              <MedicalConditionsFooter
                trustpilotImage={selectedSection()?.footerTrustpilotMiniImage}
                link={selectedSection()?.footerTrustpilotMiniUrl}
              />
            ) : null)}
        </>
      )}
    </div>
  );
}

export default withBrand(RenewalRightPanelInfoCard);
